import { useEffect, useState } from "react";
import { apiFetch } from "./api-client";

// Location history can contain many original-size photos. Starting every
// request at once overwhelms mobile browsers (and queues requests behind the
// authenticated Worker/R2 read), which makes otherwise healthy photos hit the
// client timeout. Keep this shared across every photo grid in the app.
const MAX_CONCURRENT_PHOTO_REQUESTS = 4;
type QueuedPhotoRequest = {
  start: () => void;
  cancelled: boolean;
};
let activePhotoRequests = 0;
const queuedPhotoRequests: QueuedPhotoRequest[] = [];

function runNextPhotoRequest() {
  while (activePhotoRequests < MAX_CONCURRENT_PHOTO_REQUESTS && queuedPhotoRequests.length) {
    const request = queuedPhotoRequests.shift()!;
    if (request.cancelled) continue;
    activePhotoRequests += 1;
    request.start();
  }
}

function queuePhotoRequest<T>(run: () => Promise<T>) {
  let entry: QueuedPhotoRequest | undefined;
  let settled = false;
  const promise = new Promise<T>((resolve, reject) => {
    entry = {
      cancelled: false,
      start: () => {
        void run().then(resolve, reject).finally(() => {
          settled = true;
          activePhotoRequests -= 1;
          runNextPhotoRequest();
        });
      },
    };
    queuedPhotoRequests.push(entry);
    runNextPhotoRequest();
  });
  return {
    promise,
    cancel: () => {
      if (!entry || settled) return;
      entry.cancelled = true;
      const index = queuedPhotoRequests.indexOf(entry);
      if (index >= 0) queuedPhotoRequests.splice(index, 1);
    },
  };
}

export function usePhotoImage(contentUrl: string | null) {
  const [imageUrl, setImageUrl] = useState("");
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    let created = "";
    setImageUrl("");
    setError("");
    if (!contentUrl) return;
    const request = queuePhotoRequest(async () => {
      // Queue wait time is not network time. Only abort a photo that has
      // actually started loading, so a busy grid does not manufacture errors.
      const timeout = window.setTimeout(() => controller.abort(), 30_000);
      try {
        const response = await apiFetch(contentUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(response.status === 410 ? "Not copied to preview" : response.status === 404 ? "Photo file not found" : response.status === 401 ? "Sign in to view this photo" : response.status === 403 ? "Photo access denied" : "Couldn’t load photo");
        }
        const blob = await response.blob();
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setImageUrl(created);
      } catch (cause) {
        if (!cancelled) setError(controller.signal.aborted ? "Photo request timed out" : cause instanceof Error ? cause.message : "Couldn’t load photo");
      } finally {
        window.clearTimeout(timeout);
      }
    });
    void request.promise;
    return () => {
      cancelled = true;
      request.cancel();
      controller.abort();
      if (created) URL.revokeObjectURL(created);
    };
  }, [contentUrl, attempt]);
  return {
    imageUrl,
    error,
    retry: () => setAttempt((value) => value + 1),
    onImageError: () => { setImageUrl(""); setError("This photo could not be displayed"); },
  };
}
